import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight, Heart, ShoppingBag } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const ProductDetailModal = ({ product, isOpen, onClose, onContactClick, language }) => {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (!isOpen || !product) return null;

  // Get all images
  const images = product.images || [];
  const hasImages = images.length > 0;

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={handleBackdropClick}
      style={{ backdropFilter: 'blur(4px)' }}
    >
      <div
        className="relative w-full max-w-5xl bg-white rounded-lg shadow-2xl overflow-hidden"
        style={{ maxHeight: '90vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
        >
          <X size={24} style={{ color: 'var(--charcoal)' }} />
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-0" style={{ height: '90vh', overflow: 'hidden' }}>
          {/* Image Gallery Section */}
          <div className="relative bg-gray-50 flex flex-col h-full overflow-hidden">
            {hasImages ? (
              <>
                {/* Main Image Container */}
                <div className="relative flex-1 flex items-center justify-center p-8 overflow-hidden bg-gray-50">
                  {images[currentImageIndex] ? (
                    <img
                      src={images[currentImageIndex]}
                      alt={`${product.name} - Image ${currentImageIndex + 1}`}
                      className="w-auto h-auto object-contain"
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: 'calc(90vh - 150px)',
                        display: 'block'
                      }}
                      loading="eager"
                      onError={(e) => {
                        console.error('Failed to load image:', images[currentImageIndex]);
                        e.target.parentElement.innerHTML = `<div class="text-center p-8"><p style="color: #999;">Bild konnte nicht geladen werden</p></div>`;
                      }}
                    />
                  ) : (
                    <div className="text-center p-8">
                      <p style={{ color: '#999' }}>Bild wird geladen...</p>
                    </div>
                  )}
                  
                  {/* Navigation Arrows */}
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-4 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors z-10"
                      >
                        <ChevronLeft size={24} style={{ color: 'var(--charcoal)' }} />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-4 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors z-10"
                      >
                        <ChevronRight size={24} style={{ color: 'var(--charcoal)' }} />
                      </button>
                    </>
                  )}

                  {/* Image Counter */}
                  {images.length > 1 && (
                    <div className="absolute top-4 left-1/2 transform -translate-x-1/2 px-4 py-2 bg-black bg-opacity-70 text-white rounded-full text-sm font-medium z-10">
                      {currentImageIndex + 1} / {images.length}
                    </div>
                  )}
                </div>

                {/* Thumbnail Strip */}
                {images.length > 1 && (
                  <div className="flex-shrink-0 px-4 py-4 bg-white border-t border-gray-200">
                    <div className="flex gap-3 overflow-x-auto pb-2">
                      {images.map((img, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCurrentImageIndex(idx)}
                          className={`flex-shrink-0 w-20 h-20 rounded-lg border-2 overflow-hidden transition-all ${
                            idx === currentImageIndex
                              ? 'border-4'
                              : 'border-gray-300 opacity-70 hover:opacity-100'
                          }`}
                          style={{
                            borderColor: idx === currentImageIndex ? 'var(--warm-sage)' : '#d1d5db'
                          }}
                        >
                          <img
                            src={img}
                            alt={`Thumbnail ${idx + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              console.error('Thumbnail failed to load:', img);
                              e.target.style.opacity = '0.3';
                            }}
                            onLoad={() => {
                              console.log('Thumbnail loaded:', img);
                            }}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center p-8">
                <div className="text-center">
                  <ShoppingBag size={64} style={{ color: 'var(--warm-gray)', opacity: 0.3, margin: '0 auto 1rem' }} />
                  <p className="text-lg" style={{ color: 'var(--warm-gray)' }}>
                    {language === 'de' ? 'Keine Bilder verfügbar' : 'No images available'}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Product Details Section */}
          <div className="p-8 overflow-y-auto" style={{ maxHeight: '90vh' }}>
            {/* Category Badge */}
            <Badge
              style={{
                backgroundColor: 'var(--soft-gray)',
                color: 'var(--warm-gray)',
                marginBottom: '1rem'
              }}
            >
              {product.category}
            </Badge>

            {/* Product Name */}
            <h2
              className="text-3xl font-light mb-4"
              style={{ fontFamily: 'Playfair Display, serif', color: 'var(--charcoal)' }}
            >
              {product.name}
            </h2>

            {/* Condition Badge */}
            <div className="flex items-center gap-3 mb-6">
              <Badge
                style={{
                  backgroundColor:
                    product.condition === 'Excellent'
                      ? '#d4edda'
                      : product.condition === 'Very Good'
                      ? '#fff3cd'
                      : '#f8d7da',
                  color:
                    product.condition === 'Excellent'
                      ? '#155724'
                      : product.condition === 'Very Good'
                      ? '#856404'
                      : '#721c24',
                  padding: '6px 12px'
                }}
              >
                {product.condition}
              </Badge>
            </div>

            {/* Price */}
            <div className="mb-6">
              <p
                className="text-4xl font-light"
                style={{ fontFamily: 'Playfair Display, serif', color: 'var(--charcoal)' }}
              >
                CHF {product.price.toLocaleString()}
              </p>
            </div>

            {/* Description */}
            {product.description && (
              <div className="mb-6">
                <h3
                  className="text-lg font-medium mb-2"
                  style={{ color: 'var(--charcoal)' }}
                >
                  {language === 'de' ? 'Beschreibung' : 'Description'}
                </h3>
                <p style={{ color: 'var(--warm-gray)', lineHeight: '1.6' }}>
                  {product.description}
                </p>
              </div>
            )}

            {/* Features */}
            <div className="mb-8">
              <h3
                className="text-lg font-medium mb-3"
                style={{ color: 'var(--charcoal)' }}
              >
                {language === 'de' ? 'Details' : 'Details'}
              </h3>
              <ul className="space-y-2" style={{ color: 'var(--warm-gray)' }}>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>
                    {language === 'de' ? '100% Authentifiziert' : '100% Authenticated'}
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>
                    {language === 'de'
                      ? 'Professionelle Reinigung & Pflege'
                      : 'Professional cleaning & care'}
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>
                    {language === 'de'
                      ? 'Versicherter Versand verfügbar'
                      : 'Insured shipping available'}
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>
                    {language === 'de'
                      ? 'Besichtigung nach Absprache möglich'
                      : 'Viewing by arrangement possible'}
                  </span>
                </li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 mb-3">
              <Button
                onClick={() => {
                  onContactClick(product);
                  onClose();
                }}
                className="flex-1"
                style={{ backgroundColor: 'var(--warm-sage)', color: 'white' }}
              >
                {language === 'de' ? 'Kontaktieren Sie uns' : 'Contact Us'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  // Add to wishlist functionality
                  console.log('Added to wishlist:', product.id);
                }}
                className="p-3"
              >
                <Heart size={20} />
              </Button>
            </div>
            <Button
              onClick={() => {
                onClose();
                navigate(`/payment?product=${encodeURIComponent(product.name)}&price=${product.price}`);
              }}
              className="w-full"
              style={{ backgroundColor: 'var(--lv-brown)', color: 'white' }}
            >
              {language === 'de' ? 'Kaufen' : 'Buy Now'}
            </Button>

            {/* Additional Info */}
            <div className="mt-6 pt-6 border-t" style={{ borderColor: 'var(--soft-gray)' }}>
              <p className="text-sm" style={{ color: 'var(--warm-gray)' }}>
                {language === 'de'
                  ? 'Haben Sie Fragen zu diesem Artikel? Kontaktieren Sie uns für weitere Informationen oder vereinbaren Sie einen Besichtigungstermin.'
                  : 'Questions about this item? Contact us for more information or to arrange a viewing.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailModal;
